import { Link } from '@tanstack/react-router';
import { ChevronLeft, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { pathTo } from '@/lib/search';
import { useAskHandler } from './askBridge';
import { Button } from './Button';
import s from './PageHeader.module.css';

export interface Fact {
  label: ReactNode;
  value: ReactNode;
}

/**
 * The page header: eyebrow · title · status · key facts · primary actions · "Ask about this".
 * Carries the books' signature — the blue eyebrow and a heavy blue rule — used only here.
 */
export function PageHeader({
  eyebrow,
  title,
  status,
  facts,
  description,
  actions,
  back,
  ask = true,
  askPrompt,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  status?: ReactNode;
  facts?: Fact[];
  description?: ReactNode;
  actions?: ReactNode;
  back?: { label: string; to: string };
  /** Show "Ask about this" (opens the assistant with this page's context). */
  ask?: boolean;
  askPrompt?: string;
  className?: string;
}) {
  const askHandler = useAskHandler();
  return (
    <header className={cx(s.header, className)}>
      {back && (
        <Link {...pathTo(back.to)} className={s.back}>
          <ChevronLeft aria-hidden />
          {back.label}
        </Link>
      )}
      <div className={s.top}>
        <div className={s.heading}>
          {eyebrow && <div className={s.eyebrow}>{eyebrow}</div>}
          <div className={s.titleRow}>
            <h1 className={s.title}>{title}</h1>
            {status && <div className={s.status}>{status}</div>}
          </div>
        </div>
        {(actions || (ask && askHandler)) && (
          <div className={s.actions}>
            {ask && askHandler && (
              <Button variant="ghost" icon={Sparkles} onClick={() => askHandler(askPrompt)} className={s.ask}>
                {t('Ask about this')}
              </Button>
            )}
            {actions}
          </div>
        )}
      </div>
      {description && <p className={s.description}>{description}</p>}
      {facts && facts.length > 0 && (
        <dl className={s.facts}>
          {facts.map((f, i) => (
            <div key={i} className={s.fact}>
              <dt>{f.label}</dt>
              <dd>{f.value ?? '—'}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className={s.rule} aria-hidden />
    </header>
  );
}
